import { StandardEvents } from '@shopify/events';

const dialog = document.getElementById('mue-after-add');
if (dialog instanceof HTMLDialogElement) {
  const root = window.Shopify?.routes?.root || '/';
  let sequence = 0;
  let previousFocus;
  let previousOverflow;
  let controller;
  const close = () => dialog.close();
  dialog.addEventListener('click', (event) => {
    if (event.target instanceof Element && event.target.closest('[data-mue-added-close]')) close();
    if (event.target === dialog) {
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) close();
    }
  });
  dialog.addEventListener('close', () => {
    document.documentElement.style.overflow = previousOverflow ?? '';
    if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus({ preventScroll: true });
  });
  let nativeAdds = 0;
  async function showConfirmation(product, trigger, sourceDialog) {
    const current = ++sequence;
    try {
      controller?.abort();
      controller = new AbortController();
      const productId = String(product || '').split('/').pop();
      let markup;
      if (/^\d+$/.test(productId)) {
        const url = new URL(`${root}recommendations/products`, window.location.origin);
        url.searchParams.set('section_id', 'mue-after-add');
        url.searchParams.set('product_id', productId);
        url.searchParams.set('limit', '10');
        url.searchParams.set('intent', 'related');
        try {
          const response = await fetch(url, { signal: controller.signal, cache: 'no-store' });
          if (response.ok) markup = await response.text();
        } catch (error) {
          if (error.name === 'AbortError') return;
        }
      }
      let content = markup ? new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-mue-added-content]') : null;
      if (!content) {
        const url = new URL(root, window.location.origin);
        url.searchParams.set('section_id', 'mue-after-add');
        const response = await fetch(url, { signal: controller.signal, cache: 'no-store' });
        if (!response.ok) throw new Error('Cart confirmation unavailable');
        content = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-mue-added-content]');
      }
      if (!content || current !== sequence) return;
      const open = () => {
        if (current !== sequence || !dialog.isConnected) return;
        dialog.replaceChildren(content);
        if (!dialog.open) {
          previousFocus = trigger;
          previousOverflow = document.documentElement.style.overflow;
          document.documentElement.style.overflow = 'hidden';
          dialog.showModal();
        }
        dialog.querySelector('[data-mue-added-close]')?.focus({ preventScroll: true });
      };
      if (sourceDialog?.open && sourceDialog !== dialog) sourceDialog.addEventListener('close', open, { once: true });
      else open();
    } catch (error) {
      if (error.name !== 'AbortError' && current === sequence) {
        const drawer = document.getElementById('cart-drawer');
        if (typeof drawer?.open === 'function') drawer.open();
        else window.location.assign(`${root}cart`);
      }
    }
  }

  document.addEventListener(StandardEvents.cartLinesUpdate, async (event) => {
    if (event.action !== 'add' || !event.promise) return;
    const sourceDialog = event.target instanceof Element ? event.target.closest('dialog:modal') : null;
    const trigger = document.activeElement;
    nativeAdds += 1;
    try {
      const result = await event.promise;
      if (!result?.detail?.didError) await showConfirmation(result?.detail?.productId, trigger, sourceDialog);
    } catch {
      // The product form presents add-to-cart errors; do not confirm a failed add.
    } finally {
      nativeAdds -= 1;
    }
  });

  // App-managed product forms may add successfully without Shopify's theme event.
  // Observe only successful same-origin cart additions; preserve the original response.
  const originalFetch = window.fetch;
  window.fetch = async function (...args) {
    const request = args[0];
    let isCartAdd = false;
    try {
      const url = new URL(request instanceof Request ? request.url : String(request), location.href);
      const method = args[1]?.method || (request instanceof Request ? request.method : 'GET');
      isCartAdd = url.origin === location.origin && /\/cart\/add(?:\.js)?\/?$/.test(url.pathname) && method.toUpperCase() === 'POST';
    } catch { /* Other request types retain native fetch behavior. */ }
    const trigger = isCartAdd ? document.activeElement : null;
    const sourceDialog = trigger instanceof Element ? trigger.closest('dialog:modal') : null;
    const response = await originalFetch.apply(this, args);
    if (isCartAdd && response.ok && nativeAdds === 0) {
      response.clone().json().then((data) => {
        if (data.status >= 400) return;
        const product = data.product_id || data.items?.[0]?.product_id;
        void showConfirmation(product, trigger, sourceDialog);
      }).catch(() => {});
    }
    return response;
  };
}
